import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';

const client = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Set auth token
  const token = localStorage.getItem('hash_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // Set company ID from store
  const storeData = localStorage.getItem('hash_company');
  if (storeData) {
    try {
      const parsed = JSON.parse(storeData) as { state?: { selectedCompanyId?: string } };
      const companyId = parsed?.state?.selectedCompanyId;
      if (companyId) {
        config.headers['X-Company-ID'] = companyId;
      }
    } catch {
      // ignore parse errors
    }
  }

  // Set active org ID (multi-tenancy). When absent, the backend
  // resolves the user's primary org — so single-org users don't need
  // to populate this. Persisted as a flat string for fast access.
  const orgId = localStorage.getItem('maven_active_org_id');
  if (orgId) {
    config.headers['X-Active-Org-Id'] = orgId;
  }

  return config;
});

// Auto-refresh on 401: if the access token expired but a refresh token is
// still valid, transparently obtain a new pair and replay the request. We
// queue concurrent failures so we don't fire a refresh per in-flight request.
let refreshing: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem('hash_refresh_token');
  if (!refreshToken) return null;
  try {
    const response = await axios.post<{ access_token: string; refresh_token: string }>(
      '/api/v1/auth/refresh',
      { refresh_token: refreshToken },
    );
    localStorage.setItem('hash_token', response.data.access_token);
    localStorage.setItem('hash_refresh_token', response.data.refresh_token);
    return response.data.access_token;
  } catch {
    localStorage.removeItem('hash_token');
    localStorage.removeItem('hash_refresh_token');
    return null;
  }
}

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const isAuthCall =
      typeof original?.url === 'string' &&
      (original.url.includes('/auth/login') ||
        original.url.includes('/auth/refresh') ||
        original.url.includes('/auth/google') ||
        original.url.includes('/auth/verify-otp'));

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !isAuthCall
    ) {
      original._retry = true;
      if (!refreshing) refreshing = performRefresh();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers = original.headers ?? {};
        original.headers['Authorization'] = `Bearer ${newToken}`;
        return client(original);
      }
      // Refresh failed → bounce to login.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export default client;
